import moment from 'moment';
import { hasProviderDiscount } from 'models/parseTrip';

const END_DATE_FORMAT = 'YYYY-MM-DD';

// Independent toggles for the Hot Sale / Festival de Viajes campaign: the banner on the results
// page and the per-trip icon. Either can be on without the other, but both share the same campaign
// window (SPECIAL_DISCOUNT_BANNER_END_DATE) and route list (SPECIAL_DISCOUNT_BANNER_ROUTES).
const BANNER_FLAG = 'SHOW_SPECIAL_DISCOUNT_BANNER';
const ICON_FLAG = 'SHOW_SPECIAL_DISCOUNT_ICON';

// Maps a brand to its campaign config. `variant` drives the SpecialDiscountBanner appearance;
// `icon` controls which per-trip icon the brand shows ('hotsale' | 'anniversary').
// Brands not listed here do not take part in the campaign.
const VARIANT_BY_BRAND = {
  etn: { variant: 'etn', icon: 'hotsale' },
  gho: { variant: 'gho', icon: 'hotsale' },
  costaline: { variant: 'costa', icon: 'hotsale' },
  gfa: { variant: 'gfa', icon: 'hotsale' },
  rochoa: { variant: 'rochoa', icon: 'anniversary' },
};

/**
 * Get the SpecialDiscountBanner variant for a brand.
 *
 * @param {string} brand - The whitelabel brand.
 * @returns {'etn'|'gho'|'costa'|'gfa'|'rochoa'|null} The banner variant, or null when the brand is not in the campaign.
 */
export const getSpecialDiscountVariant = (brand) => VARIANT_BY_BRAND[brand]?.variant || null;

/**
 * Get the per-trip icon type for a brand.
 *
 * @param {string} brand - The whitelabel brand.
 * @returns {'hotsale'|'anniversary'|null} The icon type, or null when the brand is not in the campaign.
 */
const getSpecialDiscountIconType = (brand) => VARIANT_BY_BRAND[brand]?.icon || null;

/* -------------------------------------------------------------------------- */
/* Campaign window (shared by the banner and icon toggles)                    */
/* -------------------------------------------------------------------------- */

/**
 * Check whether a travel date falls inside the campaign window, regardless of which toggle is on.
 * The window always starts today (no start date) and ends on SPECIAL_DISCOUNT_BANNER_END_DATE
 * (YYYY-MM-DD, inclusive); an empty end date means no expiry; an invalid end date closes the window.
 *
 * @param {object} features - Whitelabel feature flags.
 * @param {string|Date|object} date - The trip's travel/departure date to evaluate.
 * @returns {boolean} True when the date is within the campaign window.
 */
const isWithinSpecialDiscountWindow = (features, date) => {
  const travelDate = moment(date);
  if (!travelDate.isValid()) return false;
  if (travelDate.isBefore(moment(), 'day')) return false;

  const endDate = features?.SPECIAL_DISCOUNT_BANNER_END_DATE;
  if (!endDate) return true;

  const parsedEndDate = moment(endDate, END_DATE_FORMAT, true);
  if (!parsedEndDate.isValid()) return false;

  return travelDate.isSameOrBefore(parsedEndDate, 'day');
};

/**
 * Check whether a toggle is on and the travel date is within the campaign window.
 *
 * @param {object} features - Whitelabel feature flags.
 * @param {string|Date|object} date - The trip's travel/departure date to evaluate.
 * @param {string} flag - The feature flag that switches the toggle on (banner or icon).
 * @returns {boolean} True when the toggle is active for the given date.
 */
const isSpecialDiscountActiveFor = (features, date, flag) =>
  Boolean(features?.[flag]) && isWithinSpecialDiscountWindow(features, date);

/**
 * Check whether the special discount banner is active for a given travel date.
 *
 * @param {object} features - Whitelabel feature flags.
 * @param {string|Date|object} date - The trip's travel/departure date to evaluate.
 * @returns {boolean} True when the banner should be shown for the given date.
 */
export const isSpecialDiscountActiveForDate = (features, date) =>
  isSpecialDiscountActiveFor(features, date, BANNER_FLAG);

/**
 * Check whether the per-trip Hot Sale icon is active for a given travel date. Independent of the
 * banner toggle.
 *
 * @param {object} features - Whitelabel feature flags.
 * @param {string|Date|object} date - The trip's travel/departure date to evaluate.
 * @returns {boolean} True when the per-trip icon should be shown for the given date.
 */
export const isSpecialDiscountIconActiveForDate = (features, date) =>
  isSpecialDiscountActiveFor(features, date, ICON_FLAG);

/* -------------------------------------------------------------------------- */
/* Route list (shared by the banner and icon toggles)                         */
/* -------------------------------------------------------------------------- */

/**
 * Parse the SPECIAL_DISCOUNT_BANNER_ROUTES value ("ORIG-DEST,ORIG-DEST") into pairs of
 * uppercased terminal codes.
 *
 * @param {string} value - The comma-separated route list.
 * @returns {Array<string[]>} Array of [origin, destination] terminal code pairs.
 */
const parseSpecialDiscountRoutes = (value) =>
  String(value || '')
    .toUpperCase()
    .split(',')
    .map((route) => route.trim())
    .filter(Boolean)
    .map((route) => route.split('-').map((code) => code.trim()));

/**
 * Check whether the origin/destination terminal pair is allowed by the campaign route list
 * (SPECIAL_DISCOUNT_BANNER_ROUTES). Matching is direction-specific (an "A-B" entry matches only
 * A->B); add the reverse entry "B-A" to also cover the return leg. An empty route list allows all.
 *
 * @param {object} features - Whitelabel feature flags.
 * @param {string} originCode - The trip's origin terminal code.
 * @param {string} destinationCode - The trip's destination terminal code.
 * @returns {boolean} True when the route is allowed.
 */
export const isSpecialDiscountRouteAllowed = (features, originCode, destinationCode) => {
  const routes = parseSpecialDiscountRoutes(features?.SPECIAL_DISCOUNT_BANNER_ROUTES);
  if (!routes.length) return true;

  const origin = String(originCode || '').toUpperCase();
  const destination = String(destinationCode || '').toUpperCase();

  return routes.some(([from, to]) => from === origin && to === destination);
};

/* -------------------------------------------------------------------------- */
/* Trip eligibility                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Check whether a trip is eligible for the campaign, independent of any toggle and of the travel
 * date: an allowed route, a campaign brand and a provider discount.
 *
 * @param {object} params - features, brand, providerDiscount, originCode, destinationCode.
 * @param {object} params.features - Whitelabel feature flags.
 * @param {string} params.brand - The whitelabel brand.
 * @param {object|null} params.providerDiscount - The trip pricing provider discount object ({ amount }), or null.
 * @param {string} params.originCode - The trip's origin terminal code.
 * @param {string} params.destinationCode - The trip's destination terminal code.
 * @returns {boolean} True when the trip is eligible for the campaign.
 */
const tripIsSpecialDiscountEligible = ({
  features,
  brand,
  providerDiscount,
  originCode,
  destinationCode,
}) => {
  if (!getSpecialDiscountVariant(brand)) return false;
  if (!isSpecialDiscountRouteAllowed(features, originCode, destinationCode)) return false;
  // Anniversary brands (e.g. Ochoa) send provider_discount: {amount: 0} for discounted trips.
  // Use object presence as the discount signal instead of the amount.
  if (getSpecialDiscountIconType(brand) === 'anniversary') return Boolean(providerDiscount);
  return hasProviderDiscount(providerDiscount);
};

/**
 * Check whether a trip should display the per-trip Hot Sale icon. Requires the icon toggle active
 * for the trip's travel date (independent of the banner) and the trip eligible for the campaign.
 *
 * @param {object} params - features, brand, providerDiscount, departureDate, originCode, destinationCode.
 * @param {object} params.features - Whitelabel feature flags.
 * @param {string} params.brand - The whitelabel brand.
 * @param {object} params.providerDiscount - The trip pricing provider discount object ({ amount }).
 * @param {string|Date|object} params.departureDate - The trip's departure (travel) date.
 * @param {string} params.originCode - The trip's origin terminal code.
 * @param {string} params.destinationCode - The trip's destination terminal code.
 * @returns {boolean} True when the trip qualifies for the Hot Sale icon.
 */
export const tripHasHotSaleDiscount = ({ features, departureDate, ...eligibility }) =>
  isSpecialDiscountIconActiveForDate(features, departureDate) &&
  tripIsSpecialDiscountEligible({ features, ...eligibility }) &&
  getSpecialDiscountIconType(eligibility.brand) === 'hotsale';

/**
 * Check whether a trip should display the anniversary confetti icon. Requires the icon
 * toggle active for the trip's travel date and the trip eligible for the campaign (anniversary
 * icon brand, allowed route, provider discount).
 *
 * @param {object} params - features, brand, providerDiscount, departureDate, originCode, destinationCode.
 * @param {object} params.features - Whitelabel feature flags.
 * @param {string} params.brand - The whitelabel brand.
 * @param {object} params.providerDiscount - The trip pricing provider discount object ({ amount }).
 * @param {string|Date|object} params.departureDate - The trip's departure (travel) date.
 * @param {string} params.originCode - The trip's origin terminal code.
 * @param {string} params.destinationCode - The trip's destination terminal code.
 * @returns {boolean} True when the trip qualifies for the Rochoa anniversary icon.
 */
export const tripHasAnniversaryIcon = ({ features, departureDate, ...eligibility }) =>
  getSpecialDiscountIconType(eligibility.brand) === 'anniversary' &&
  isSpecialDiscountIconActiveForDate(features, departureDate) &&
  tripIsSpecialDiscountEligible({ features, ...eligibility });

/**
 * Resolve the props that drive the special discount banner on a search results page. The banner is
 * shown when the brand takes part in the campaign and at least one trip qualifies: the banner toggle
 * is active for its travel date, the trip is eligible (allowed route + provider discount). Shared by
 * the departures and returns containers.
 *
 * @param {object} params - features, brand, tripList, pricingKey.
 * @param {object} params.features - Whitelabel feature flags.
 * @param {string} params.brand - The whitelabel brand.
 * @param {import('immutable').List} [params.tripList] - Immutable list of trips for the search.
 * @param {string} params.pricingKey - Pricing key holding the provider discount for the trip's
 *   way ('pricing' | 'departureRoundTripPricing' | 'roundTripPricing').
 * @returns {{ specialDiscountVariant: ('etn'|'gho'|'costa'|'gfa'|'rochoa'|null), showSpecialDiscountBanner: boolean }}
 */
export const resolveSpecialDiscountBannerProps = ({ features, brand, tripList, pricingKey }) => {
  const specialDiscountVariant = getSpecialDiscountVariant(brand);

  /**
   * Determine whether a single trip qualifies for the Hot Sale banner.
   * @param {import('immutable').Map} trip - Trip from the immutable list.
   * @returns {boolean} True when the trip meets Hot Sale criteria.
   */
  const tripQualifies = (trip) =>
    isSpecialDiscountActiveForDate(features, trip.get('departure')) &&
    tripIsSpecialDiscountEligible({
      features,
      brand,
      providerDiscount: trip.getIn([pricingKey, 'providerDiscount'])
        ? { amount: trip.getIn([pricingKey, 'providerDiscount', 'amount']) }
        : null,
      originCode: trip.get('originCode'),
      destinationCode: trip.get('destinationCode'),
    });

  const showSpecialDiscountBanner = Boolean(tripList?.some(tripQualifies));

  return { specialDiscountVariant, showSpecialDiscountBanner };
};
